// screens/staff/LacuneQueueScreen.tsx
//
// W6 v1 Session 3 — content gap queue per spec §5.4.
//
// Visible to titolare + instructor_teoria + instructor_guida (segreteria
// gets 403 from the BE + the side-nav hides "Lacune"). Italian "Lei" voice.
//
// Per spec §5.4 the three actions are VERBATIM:
//   1. Rispondi allo studente
//   2. Segnala al team curriculum
//   3. Archivia
//
// Each action opens a modal (dismissable via Esc + Cancel button) with
// formal Italian copy + a final CTA. Keep the entered text until the
// transaction is acknowledged; an identical retry cannot duplicate delivery.
//
// W6 v1.1 Session 2: student-side delivery wired. The Rispondi handler
// inserts a student_messages row (kind=instructor_response,
// source_kind=gap_response) alongside the existing brain_gaps.context.response
// write — the student sees the response in /messaggi. The disclaimer copy
// in the Rispondi modal now reflects this.

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { staffApi, type GapQueueRow } from '../../api/staff';
import { StaffApiError } from '../../api/staff-client';

type ModalKind = 'respond' | 'escalate' | 'archive' | null;

/** W7 Session 3 — "Genera bozza" inline state per gap row. The toast
 *  area at the top of the screen surfaces a single message at a time;
 *  the per-row in-flight flag disables the button to prevent double
 *  submissions while the LLM call is running. */
interface GeneraBozzaToast {
  kind: 'info' | 'success' | 'error';
  text: string;
  /** Optional link target shown alongside the success message. */
  link?: { href: string; label: string };
}

interface OpenModal {
  kind: ModalKind;
  gap:  GapQueueRow | null;
}

const STATUS_LABEL: Record<string, string> = {
  open:      'Da rispondere',
  in_review: 'In valutazione',
  resolved:  'Risolta',
  archived:  'Archiviata',
};

export default function LacuneQueueScreen(): JSX.Element {
  const tokens = useTokens();
  const [gaps, setGaps] = useState<GapQueueRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const actionBusyRef = useRef(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [modal, setModal] = useState<OpenModal>({ kind: null, gap: null });
  // W7 Session 3 — "Genera bozza" toast + per-row in-flight tracker.
  const [generaToast, setGeneraToast] = useState<GeneraBozzaToast | null>(null);
  const [generaInFlight, setGeneraInFlight] = useState<Set<string>>(new Set());

  const statuses = useMemo(
    () => (showResolved ? ['open', 'in_review', 'resolved', 'archived'] as const : ['open', 'in_review'] as const),
    [showResolved],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    staffApi.listGapQueue({ statuses: [...statuses], page })
      .then((res) => {
        if (!alive) return;
        setGaps(res.gaps);
        setHasMore(res.pagination.has_more);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Si è verificato un errore. Riprovi tra poco.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [statuses, page]);

  function refresh() {
    // Clear cached state and trigger the fetch effect again.
    setGaps(null);
    setLoading(true);
    setExpanded(null);
    setActionError(null);
    setPage(p => p);  // no-op trigger; statuses dependency captures the rest
    staffApi.listGapQueue({ statuses: [...statuses], page })
      .then((res) => { setGaps(res.gaps); setHasMore(res.pagination.has_more); setLoading(false); })
      .catch(() => { setError('Si è verificato un errore. Riprovi tra poco.'); setLoading(false); });
  }

  function openModal(kind: Exclude<ModalKind, null>, gap: GapQueueRow) {
    setActionError(null);
    setModal({ kind, gap });
  }

  function closeModal() {
    if (actionBusyRef.current) return;
    setModal({ kind: null, gap: null });
  }

  async function runAction(action: Exclude<ModalKind, null>, text?: string, notes?: string | null) {
    if (!modal.gap || actionBusyRef.current) return;
    const gapId = modal.gap.id;
    actionBusyRef.current = true;
    setActionBusy(true);
    setActionError(null);
    try {
      if (action === 'respond') await staffApi.respondToGap(gapId, { response_text: text! });
      else if (action === 'escalate') await staffApi.escalateGap(gapId, notes ? { notes } : undefined);
      else await staffApi.archiveGap(gapId);
      setModal({ kind: null, gap: null });
      refresh();
    } catch (err) {
      if (err instanceof StaffApiError && (err.status === 409 || err.status === 404)) {
        setModal({ kind: null, gap: null });
        refresh();
        setActionError(err.status === 409
          ? 'Questa lacuna è già stata gestita. Verifichi il suo stato nella coda.'
          : 'Questa lacuna non è più disponibile.');
      } else {
        setActionError(action === 'respond'
          ? 'Invio non confermato. La risposta è conservata qui: riprovi per confermare l’invio senza duplicarlo.'
          : 'Operazione non confermata. Riprovi: la stessa azione non verrà eseguita due volte.');
      }
    } finally {
      actionBusyRef.current = false;
      setActionBusy(false);
    }
  }

  const handleRespond = (text: string) => runAction('respond', text);
  const handleEscalate = (notes: string | null) => runAction('escalate', undefined, notes);
  const handleArchive = () => runAction('archive');

  /**
   * W7 Session 3 — "Genera bozza" handler. POST /api/corpus/research/from-gap
   * with `{gap_id}` → on 201, surface a success toast linking to /staff/bozze.
   * Distinguishes 400 (gap_lacks_principle_context — suggest upload widget),
   * 502 (LLM unavailable), and other errors.
   */
  async function handleGeneraBozza(gap: GapQueueRow) {
    // Surface the in-flight toast immediately so the instructor sees the
    // call kicked off — Anthropic latency is 5-15s typically.
    setGeneraToast({
      kind: 'info',
      text: 'Genero una bozza dal contenuto della lacuna…',
    });
    setGeneraInFlight((prev) => {
      const next = new Set(prev);
      next.add(gap.id);
      return next;
    });
    try {
      const res = await staffApi.researchFromGap({ gap_id: gap.id });
      setGeneraToast({
        kind: 'success',
        text: 'Bozza generata. La apra per rivedere.',
        link: {
          href: `/staff/bozze?id=${encodeURIComponent(res.draft.id)}`,
          label: 'Vai alla bozza',
        },
      });
    } catch (err) {
      if (err instanceof StaffApiError) {
        // 400 with reason='gap_lacks_principle_context' → suggest upload widget.
        const body = err.body as { reason?: string; message?: string } | null;
        if (err.status === 400 && body?.reason === 'gap_lacks_principle_context') {
          setGeneraToast({
            kind: 'error',
            text: 'Questa lacuna non ha un principio collegato chiaro. Carichi un documento dal pannello Bozze per arricchire un principio specifico.',
          });
        } else if (err.status === 502) {
          setGeneraToast({
            kind: 'error',
            text: 'Il modello non è disponibile. Riprovi tra qualche minuto.',
          });
        } else if (err.status === 404) {
          setGeneraToast({
            kind: 'error',
            text: 'Lacuna non trovata.',
          });
        } else {
          setGeneraToast({
            kind: 'error',
            text: 'Errore durante la generazione. Riprovi.',
          });
        }
      } else {
        setGeneraToast({
          kind: 'error',
          text: 'Errore durante la generazione. Riprovi.',
        });
      }
    } finally {
      setGeneraInFlight((prev) => {
        const next = new Set(prev);
        next.delete(gap.id);
        return next;
      });
    }
  }

  return (
    <section data-testid="lacune-queue-screen">
      <header style={{ marginBottom: 24 }}>
        {/* Title aligned with the dashboard card ("LACUNE CONTENUTI") + the
            side-nav ("Lacune") — three identical UI strings instead of three
            slightly-different ones. */}
        <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>
          Lacune contenuti
        </h1>
        <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
          Domande degli studenti che il Prof non ha potuto coprire con i principi validati.
          Ogni voce è un'opportunità per arricchire il corso.
        </p>
      </header>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: tokens.textMuted, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            data-testid="lacune-show-resolved"
            checked={showResolved}
            onChange={(e) => { setShowResolved(e.target.checked); setPage(1); }}
          />
          Mostra anche voci già risolte / archiviate
        </label>
      </div>

      {actionError && !modal.kind && (
        <div role="alert" data-testid="lacune-action-error" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>
          {actionError}
        </div>
      )}
      {error && (
        <div role="alert" data-testid="lacune-error" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>
          {error}
        </div>
      )}
      {generaToast && (
        <div
          role={generaToast.kind === 'error' ? 'alert' : 'status'}
          data-testid={`lacune-genera-toast-${generaToast.kind}`}
          style={{
            marginBottom: 16,
            color:
              generaToast.kind === 'error'
                ? tokens.wrong
                : generaToast.kind === 'success'
                  ? tokens.right
                  : tokens.text,
            background: tokens.drawerBg,
            border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding: '10px 14px',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{generaToast.text}</span>
          <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {generaToast.link && (
              <Link
                data-testid="lacune-genera-toast-link"
                to={generaToast.link.href}
                style={{ color: tokens.accent, textDecoration: 'underline', fontSize: 13 }}
              >
                {generaToast.link.label}
              </Link>
            )}
            <button
              data-testid="lacune-genera-toast-dismiss"
              onClick={() => setGeneraToast(null)}
              aria-label="Chiudi notifica"
              style={{
                background: 'transparent',
                color: tokens.textMuted,
                border: 'none',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
                padding: 4,
              }}
            >
              ×
            </button>
          </span>
        </div>
      )}

      {loading ? (
        <div data-testid="lacune-loading" style={{ color: tokens.textMuted, fontSize: 13 }}>
          Caricamento lacune…
        </div>
      ) : gaps && gaps.length === 0 ? (
        <div data-testid="lacune-empty" style={{ color: tokens.textMuted, fontSize: 14, padding: '32px 0' }}>
          Nessuna lacuna in coda. Buon lavoro.
        </div>
      ) : gaps && (
        <div data-testid="lacune-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {gaps.map((gap) => (
            <GapRow
              key={gap.id}
              gap={gap}
              expanded={expanded === gap.id}
              onToggle={() => setExpanded(expanded === gap.id ? null : gap.id)}
              onRespond={() => openModal('respond', gap)}
              onEscalate={() => openModal('escalate', gap)}
              onArchive={() => openModal('archive', gap)}
              onGeneraBozza={() => handleGeneraBozza(gap)}
              generaInFlight={generaInFlight.has(gap.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {gaps && (page > 1 || hasMore) && (
        <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            data-testid="lacune-prev"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={navButtonStyle(tokens, page <= 1)}
          >
            ← Pagina precedente
          </button>
          <span style={{ fontSize: 13, color: tokens.textMuted }}>Pagina {page}</span>
          <button
            data-testid="lacune-next"
            disabled={!hasMore}
            onClick={() => setPage(p => p + 1)}
            style={navButtonStyle(tokens, !hasMore)}
          >
            Pagina successiva →
          </button>
        </div>
      )}

      {/* Modals */}
      {modal.kind === 'respond' && modal.gap && (
        <RespondModal busy={actionBusy} error={actionError} gap={modal.gap} onCancel={closeModal} onConfirm={handleRespond} />
      )}
      {modal.kind === 'escalate' && modal.gap && (
        <EscalateModal busy={actionBusy} error={actionError} gap={modal.gap} onCancel={closeModal} onConfirm={handleEscalate} />
      )}
      {modal.kind === 'archive' && modal.gap && (
        <ArchiveModal busy={actionBusy} error={actionError} gap={modal.gap} onCancel={closeModal} onConfirm={handleArchive} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Gap row
// ---------------------------------------------------------------------------

function GapRow({
  gap,
  expanded,
  onToggle,
  onRespond,
  onEscalate,
  onArchive,
  onGeneraBozza,
  generaInFlight,
}: {
  gap:            GapQueueRow;
  expanded:       boolean;
  onToggle:       () => void;
  onRespond:      () => void;
  onEscalate:     () => void;
  onArchive:      () => void;
  /** W7 Session 3 — fires the from-gap research pass for this gap. */
  onGeneraBozza:  () => void;
  /** W7 Session 3 — disables the button while the LLM call is in flight,
   *  preventing double-submission during the 5-15s Anthropic latency window. */
  generaInFlight: boolean;
}): JSX.Element {
  const tokens = useTokens();
  const studentName = gap.student_first_name
    ? gap.student_first_name + (gap.student_last_name ? ` ${gap.student_last_name}` : '')
    : 'studente sconosciuto';
  const isMutable = gap.status === 'open' || gap.status === 'in_review';
  return (
    <div
      data-testid={`gap-row-${gap.id}`}
      // Esc collapses the expander when open — mirrors the modal Esc-dismissal
      // pattern so a keyboard user has a consistent way to back out of any
      // expanded surface without having to find the "Riduci" button.
      onKeyDown={(e) => {
        if (e.key === 'Escape' && expanded) {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        background:    tokens.surface,
        border:        `1px solid ${tokens.line}`,
        borderRadius:  tokens.radius.lg,
        padding:       '16px 18px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <span data-testid={`gap-status-${gap.id}`} style={statusBadgeStyle(tokens, gap.status)}>
              {STATUS_LABEL[gap.status] ?? gap.status}
            </span>
            <span style={{ fontSize: 13, color: tokens.textMuted }}>
              {formatDate(gap.created_at)}
            </span>
            <span style={{ fontSize: 13, color: tokens.textMuted }}>
              · {studentName}
            </span>
          </div>
          <div style={{ fontSize: 14, color: tokens.text, marginBottom: 6 }}>
            {gap.description}
          </div>
        </div>
        <button
          data-testid={`gap-toggle-${gap.id}`}
          onClick={onToggle}
          style={{
            background:   'transparent',
            color:        tokens.textMuted,
            border:       `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding:      '6px 12px',
            cursor:       'pointer',
            fontSize:     12,
          }}
          aria-expanded={expanded}
        >
          {expanded ? 'Riduci' : 'Visualizza dettagli'}
        </button>
      </div>

      {expanded && (
        <div data-testid={`gap-detail-${gap.id}`} style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tokens.line}` }}>
          {gap.retrieval_attempt_summary && (
            <div style={{ fontSize: 13, color: tokens.textMuted, marginBottom: 12 }}>
              {gap.retrieval_attempt_summary}
            </div>
          )}
          {gap.context.question != null && (
            <div style={{ fontSize: 13, color: tokens.text, marginBottom: 12, fontStyle: 'italic' }}>
              «{String(gap.context.question)}»
            </div>
          )}

          {/* Show resolved-state metadata when applicable */}
          {gap.status === 'resolved' && gap.context.response != null && (
            <div data-testid={`gap-resolution-${gap.id}`} style={{ fontSize: 13, color: tokens.text, padding: '10px 12px', background: tokens.drawerBg, borderRadius: tokens.radius.md, marginBottom: 12 }}>
              <strong>
                {/* Render author name when available — falls back to "Risposta:"
                    when the staff_id is unknown to this school (cross-school
                    audit transparency). */}
                {gap.resolved_by_first_name
                  ? `Risposta di ${gap.resolved_by_first_name}${gap.resolved_by_last_name ? ' ' + gap.resolved_by_last_name : ''}:`
                  : 'Risposta:'}
              </strong>{' '}
              {String((gap.context.response as { text?: string })?.text ?? '')}
            </div>
          )}
          {gap.status === 'resolved' && gap.context.escalation != null && (
            <div data-testid={`gap-escalation-${gap.id}`} style={{ fontSize: 13, color: tokens.textMuted, padding: '10px 12px', background: tokens.drawerBg, borderRadius: tokens.radius.md, marginBottom: 12 }}>
              <strong>Segnalata al team curriculum.</strong>
              {(gap.context.escalation as { notes?: string })?.notes && (
                <div style={{ marginTop: 4 }}>{String((gap.context.escalation as { notes?: string }).notes)}</div>
              )}
            </div>
          )}

          {isMutable && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                data-testid={`gap-action-respond-${gap.id}`}
                onClick={onRespond}
                style={primaryButtonStyle(tokens)}
              >
                Rispondi allo studente
              </button>
              <button
                data-testid={`gap-action-escalate-${gap.id}`}
                onClick={onEscalate}
                style={secondaryButtonStyle(tokens)}
              >
                Segnala al team curriculum
              </button>
              <button
                data-testid={`gap-action-archive-${gap.id}`}
                onClick={onArchive}
                style={secondaryButtonStyle(tokens)}
              >
                Archivia
              </button>
              {/* W7 Session 3 — "Genera bozza": run an LLM pass on the
                  refused question + nearest principle, persist a draft
                  for /staff/bozze review. Disabled during in-flight to
                  prevent double-submission. */}
              <button
                data-testid={`gap-action-genera-bozza-${gap.id}`}
                aria-label="Genera bozza per questa lacuna"
                onClick={onGeneraBozza}
                disabled={generaInFlight}
                style={secondaryButtonStyle(tokens, generaInFlight)}
              >
                {generaInFlight ? 'Genero…' : 'Genera bozza'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

function ModalShell({
  testid,
  title,
  children,
  onCancel,
  error,
}: {
  error?: string | null;
  testid:   string;
  title:    string;
  children: React.ReactNode;
  onCancel: () => void;
}): JSX.Element {
  const tokens = useTokens();
  // Esc closes the modal. Focus the modal on mount + restore on unmount.
  // TODO v1.1: add Tab focus-trap so keyboard focus can't leak to the
  // underlying page (mirrors the three Session 1 Segreteria modals — same
  // a11y polish gap, same v1.1 deferral). Esc dismissal + initial focus
  // are sufficient for v1; the trap is a 9-month-out hardening item.
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);
  useEffect(() => {
    lastFocused.current = document.activeElement;
    const node = ref.current;
    if (node) {
      const initial = node.querySelector<HTMLElement>('[data-modal-initial-focus]') ?? node;
      initial.focus();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus();
    };
  }, [onCancel]);

  return (
    <div
      data-testid={`${testid}-backdrop`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testid}-title`}
      style={{
        position:  'fixed',
        inset:     0,
        background: 'rgba(0, 0, 0, 0.55)',
        display:   'flex',
        alignItems:'center',
        justifyContent: 'center',
        zIndex:    1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        ref={ref}
        data-testid={testid}
        tabIndex={-1}
        style={{
          background:    tokens.surface,
          color:         tokens.text,
          borderRadius:  tokens.radius.lg,
          padding:       '24px 28px',
          maxWidth:      560,
          width:         '90%',
          maxHeight:     '85vh',
          overflowY:     'auto',
          fontFamily:    tokens.fonts.body,
        }}
      >
        <h2 id={`${testid}-title`} style={{ fontFamily: tokens.fonts.serif, fontSize: 22, fontWeight: 700, margin: '0 0 12px 0' }}>
          {title}
        </h2>
        {error && <p role="alert" data-testid="lacune-modal-action-error" style={{ color: tokens.wrong, fontSize: 13 }}>{error}</p>}
        {children}
      </div>
    </div>
  );
}

type ActionModalState = { busy: boolean; error: string | null };

function RespondModal({ gap, onCancel, onConfirm, busy, error }: ActionModalState & { gap: GapQueueRow; onCancel: () => void; onConfirm: (text: string) => void }): JSX.Element {
  const tokens = useTokens();
  const [text, setText] = useState<string>('');
  const valid = text.trim().length > 0;
  return (
    <ModalShell error={error} testid="modal-respond" title="Rispondi allo studente" onCancel={onCancel}>
      <p style={{ color: tokens.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>
        Scriva qui la risposta. La sua risposta resta legata alla lacuna nel sistema; il team curriculum la consulterà per arricchire il corso.
      </p>
      {gap.context.question != null && (
        <div style={{ fontSize: 13, color: tokens.text, marginBottom: 12, fontStyle: 'italic', padding: '10px 12px', background: tokens.drawerBg, borderRadius: tokens.radius.md }}>
          «{String(gap.context.question)}»
        </div>
      )}
      <textarea
        data-testid="modal-respond-textarea"
        data-modal-initial-focus
        disabled={busy}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder="La sua risposta…"
        style={{
          width:         '100%',
          background:    tokens.bg,
          color:         tokens.text,
          border:        `1px solid ${tokens.line}`,
          borderRadius:  tokens.radius.md,
          padding:       '10px 12px',
          fontFamily:    tokens.fonts.body,
          fontSize:      14,
          resize:        'vertical',
        }}
      />
      <p style={{ fontSize: 12, color: tokens.textMuted, fontStyle: 'italic', margin: '8px 0 16px 0' }}>
        La risposta verrà recapitata allo studente nella sua casella messaggi.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button data-testid="modal-respond-cancel" disabled={busy} onClick={onCancel} style={secondaryButtonStyle(tokens)}>
          Annulla
        </button>
        <button
          data-testid="modal-respond-confirm"
          disabled={!valid || busy}
          onClick={() => onConfirm(text.trim())}
          style={primaryButtonStyle(tokens, !valid)}
        >
          {busy ? 'Invio in corso…' : error ? 'Riprova invio' : 'Salva risposta'}
        </button>
      </div>
    </ModalShell>
  );
}

function EscalateModal({ gap, onCancel, onConfirm, busy, error }: ActionModalState & { gap: GapQueueRow; onCancel: () => void; onConfirm: (notes: string | null) => void }): JSX.Element {
  const tokens = useTokens();
  const [notes, setNotes] = useState<string>('');
  return (
    <ModalShell error={error} testid="modal-escalate" title="Segnala al team curriculum" onCancel={onCancel}>
      <p style={{ color: tokens.textMuted, fontSize: 13, margin: '0 0 16px 0' }}>
        Segnali questa lacuna al team che cura il corso. Aggiunga una nota se utile (facoltativa).
      </p>
      {gap.context.question != null && (
        <div style={{ fontSize: 13, color: tokens.text, marginBottom: 12, fontStyle: 'italic', padding: '10px 12px', background: tokens.drawerBg, borderRadius: tokens.radius.md }}>
          «{String(gap.context.question)}»
        </div>
      )}
      <textarea
        data-testid="modal-escalate-textarea"
        data-modal-initial-focus
        disabled={busy}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        placeholder="Eventuali note per il team (facoltativo)…"
        style={{
          width:         '100%',
          background:    tokens.bg,
          color:         tokens.text,
          border:        `1px solid ${tokens.line}`,
          borderRadius:  tokens.radius.md,
          padding:       '10px 12px',
          fontFamily:    tokens.fonts.body,
          fontSize:      14,
          resize:        'vertical',
          marginBottom:  16,
        }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button data-testid="modal-escalate-cancel" disabled={busy} onClick={onCancel} style={secondaryButtonStyle(tokens)}>
          Annulla
        </button>
        <button
          data-testid="modal-escalate-confirm"
          disabled={busy}
          onClick={() => onConfirm(notes.trim() || null)}
          style={primaryButtonStyle(tokens)}
        >
          Conferma segnalazione
        </button>
      </div>
    </ModalShell>
  );
}

function ArchiveModal({ gap, onCancel, onConfirm, busy, error }: ActionModalState & { gap: GapQueueRow; onCancel: () => void; onConfirm: () => void }): JSX.Element {
  const tokens = useTokens();
  return (
    <ModalShell error={error} testid="modal-archive" title="Archivia lacuna" onCancel={onCancel}>
      <p style={{ color: tokens.text, fontSize: 14, margin: '0 0 16px 0' }}>
        Conferma di voler archiviare questa lacuna come fuori tema. La voce resterà consultabile tra le lacune gestite.
      </p>
      {gap.context.question != null && (
        <div style={{ fontSize: 13, color: tokens.textMuted, marginBottom: 16, fontStyle: 'italic' }}>
          «{String(gap.context.question)}»
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button data-testid="modal-archive-cancel" disabled={busy} data-modal-initial-focus onClick={onCancel} style={secondaryButtonStyle(tokens)}>
          Annulla
        </button>
        <button data-testid="modal-archive-confirm" disabled={busy} onClick={onConfirm} style={primaryButtonStyle(tokens)}>
          Conferma archiviazione
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function primaryButtonStyle(tokens: ReturnType<typeof useTokens>, disabled = false): React.CSSProperties {
  return {
    background:   disabled ? tokens.line : tokens.accent,
    color:        disabled ? tokens.textMuted : (tokens.mode === 'dark' ? '#1a1510' : '#fff'),
    border:       'none',
    borderRadius: tokens.radius.pill,
    padding:      '10px 20px',
    cursor:       disabled ? 'not-allowed' : 'pointer',
    fontSize:     13,
    fontWeight:   600,
  };
}

function secondaryButtonStyle(
  tokens: ReturnType<typeof useTokens>,
  disabled = false,
): React.CSSProperties {
  return {
    background:   'transparent',
    color:        disabled ? tokens.textMuted : tokens.text,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.pill,
    padding:      '10px 20px',
    cursor:       disabled ? 'not-allowed' : 'pointer',
    fontSize:     13,
    opacity:      disabled ? 0.6 : 1,
  };
}

function navButtonStyle(tokens: ReturnType<typeof useTokens>, disabled: boolean): React.CSSProperties {
  return {
    background:   'transparent',
    color:        disabled ? tokens.textMuted : tokens.text,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.md,
    padding:      '8px 14px',
    cursor:       disabled ? 'not-allowed' : 'pointer',
    fontSize:     13,
    opacity:      disabled ? 0.55 : 1,
  };
}

function statusBadgeStyle(tokens: ReturnType<typeof useTokens>, status: string): React.CSSProperties {
  let bg: string = tokens.drawerBg;
  let color: string = tokens.textMuted;
  if (status === 'open')      { bg = tokens.accentSoft; color = tokens.accentInk; }
  if (status === 'in_review') { color = tokens.text; }
  if (status === 'resolved')  { color = tokens.right; }
  if (status === 'archived')  { color = tokens.textMuted; }
  return {
    background:   bg,
    color,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.pill,
    padding:      '3px 10px',
    fontSize:     11,
    fontWeight:   600,
    letterSpacing: 0.2,
  };
}

function formatDate(iso: string): string {
  // dd/mm/yyyy in Italian locale
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
